import { CustomValidator } from "../../common/validation/types";
import { AuthorComposerValuesType } from "./AuthorComposerCommonStore";
import { PublisherValuesType } from "../../publisher/store/PublisherCommonStore";
import type { ReferenceCacheStore } from "../../../store/ReferenceCacheStore";
import { identifierChanged, identifierFeedback } from "../../common/validation/identifierValidation";

/** Compara CUIT/CUIL sin máscara (solo dígitos). */
const normalizeTaxId = (v: string | null | undefined): string => (v ?? "").replace(/\D/g, "");

/**
 * Espejo cliente (UX) de la regla del server
 * (assertNoNaturalPersonPublisherSharingAuthorCuit): no se puede asociar al autor
 * una editorial persona física con el mismo CUIT/CUIL que el autor — un editor
 * persona física no puede publicar sus propias obras. El server es la autoridad;
 * esto da feedback inmediato. Lee personType/taxId de cada editorial referenciada
 * desde el cache de Publisher (.cacheable); si el cache aún no cargó, degrada al
 * gate del server.
 */
export const authorNoSelfPublisherValidator: CustomValidator<AuthorComposerValuesType> = {
  name: "authorNoSelfPublisherValidator",
  validate(values, langStore, cache?: ReferenceCacheStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const authorCuit = normalizeTaxId(values.taxId);
    if ((values.taxIdType && values.taxIdType !== "cuitCuil") || !authorCuit || !cache) {
      return { errors, cardErrors };
    }

    const liveRows = (values.showPublishers ?? []).filter(
      (r) => !r.__status__.endsWith("deleted"),
    );
    if (liveRows.length === 0) return { errors, cardErrors };

    const publishers = cache.getOptions("Publisher") as unknown as PublisherValuesType[];
    const byId = new Map(publishers.map((p) => [p.id, p]));

    const conflict = liveRows.some((r) => {
      const id = r.showPublisher?.id;
      if (!id) return false;
      const p = byId.get(id);
      return !!p && p.personType === "naturalPerson" && normalizeTaxId(p.taxId) === authorCuit;
    });

    if (conflict) {
      errors.publishers = {
        message: langStore.authorPublisherSameCuit,
        fieldLabel: langStore.AuthorComposerPublishersLabel,
      };
    }

    return { errors, cardErrors };
  },
};

const hasValue = (v: unknown): boolean => v !== null && v !== undefined && v !== "";

/**
 * Espejo cliente (UX) de la regla del server (assertRenewalRequiresDuration): no se
 * puede definir renovación automática sin un límite de duración. Si una participación
 * tiene duración "Sin límite" (vacía) y renovación con valor → error.
 */
export const authorRenewalRequiresDurationValidator: CustomValidator<AuthorComposerValuesType> = {
  name: "authorRenewalRequiresDurationValidator",
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const liveRows = (values.showPublishers ?? []).filter(
      (r) => !r.__status__.endsWith("deleted"),
    );
    const conflict = liveRows.some(
      (r) => !hasValue(r.durationInYears) && hasValue(r.autoRenewalYears),
    );

    if (conflict) {
      const message = {
        es: "No se puede definir una renovación automática sin un límite de duración: si la duración es 'Sin límite de duración', la renovación debe ser 'NO'.",
        en: "Automatic renewal can't be set without a duration limit: if the duration is 'No limit', the renewal must be 'NO'.",
        pt: "Não é possível definir renovação automática sem um limite de duração: se a duração é 'Sem limite', a renovação deve ser 'NÃO'.",
      }[langStore.language] as string;
      errors.publishers = {
        message,
        fieldLabel: langStore.AuthorComposerPublishersLabel,
      };
    }

    return { errors, cardErrors };
  },
};

/**
 * Cambió: comparación por valor. Los campos congelados son textos (y la
 * nacionalidad, su id); la fecha de nacimiento no se congela.
 */
const identityChanged = (a: unknown, b: unknown): boolean => a !== b;

/**
 * Congela la identidad del autor una vez descargada la constancia de ANSES
 * (cuitCertificate): Nombre, Apellido, CUIT/CUIL, Género y Nacionalidad NO se
 * pueden volver a editar (esos datos quedaron validados en ANSES; la
 * nacionalidad además gobierna la exigencia de la constancia). La fecha de
 * nacimiento NO se congela: ANSES no la muestra en la constancia, así que no
 * hay contra qué validarla. Compara los valores actuales contra el snapshot
 * ORIGINAL (pre-edición) que provee el update store. Solo aplica en update
 * (donde hay `original`); en create no hay constancia todavía.
 */
export const authorAnsesCertFrozenValidator: CustomValidator<AuthorComposerValuesType> = {
  name: "authorAnsesCertFrozenValidator",
  validate(values, langStore, _cache, original) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (!values.cuitCertificate || !original) {
      return { errors, cardErrors };
    }

    const frozenFields: Array<[keyof AuthorComposerValuesType, string]> = [
      ["firstName", langStore.AuthorComposerFirstNameLabel],
      ["lastName", langStore.AuthorComposerLastNameLabel],
      ["taxId", langStore.AuthorComposerTaxIdLabel],
      ["gender", langStore.AuthorComposerGenderLabel],
    ];

    for (const [field, fieldLabel] of frozenFields) {
      if (identityChanged(values[field], original[field])) {
        errors[field as string] = {
          message: langStore.authorFrozenByAnsesCert,
          fieldLabel,
        };
      }
    }

    // Nacionalidad es una referencia (Country). El snapshot `original`
    // (computeSnapshot del update store) guarda las refs como id bajo el nombre
    // plano (`nationality`), NO como el objeto `showNationality` (que solo existe
    // en los valores del form). El tipo Partial<T> no refleja esa forma, de ahí el
    // cast. Se compara el id actual contra `original.nationality`.
    const originalNationalityId = (original as Record<string, any>).nationality ?? null;
    if (identityChanged(values.showNationality?.id ?? null, originalNationalityId)) {
      errors["nationality"] = {
        message: langStore.authorFrozenByAnsesCert,
        fieldLabel: langStore.AuthorComposerNationalityLabel,
      };
    }

    return { errors, cardErrors };
  },
};

/**
 * IPI del autor: error si está mal formado, si los dígitos de control no coinciden o si
 * no está en su forma de 11 dígitos. Solo si el campo cambió contra el original: un
 * autor viejo con un IPI malo se puede seguir editando (el chequeo previo a generar la
 * documentación lo frena igual). Espejo del server (validateIpiIfChanged). Puro: no
 * escribe en `values`.
 */
export const authorComposerIpiValidator: CustomValidator<AuthorComposerValuesType> = {
  name: "authorComposerIpiValidator",
  validate(values, langStore, _cache, original) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (identifierChanged(values.ipi, original, "ipi")) {
      const { error } = identifierFeedback("ipi", values.ipi, langStore.language);
      if (error) {
        errors.ipi = { message: error, fieldLabel: langStore.AuthorComposerIpiLabel };
      }
    }

    return { errors, cardErrors };
  },
};
